import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { getDatabase, ref, set, push, update, get } from 'firebase/database';
import { School } from '../types/schools/School';
import { useSchools } from '../contexts/SchoolsContext';
import { AIDraftService, AIDraftSchool } from '../services/aiDraftService';
import { CanadianAIDraftService, CanadianAIDraftSchool } from '../services/canadianAIDraftService';
import { AthleticsDraftService, AthleticsDraftSchool } from '../services/athleticsDraftService';
import { ProgramsDraftService, ProgramsDraftSchool } from '../services/programsDraftService';

// TabPanel component for tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`school-tabpanel-${index}`}
      aria-labelledby={`school-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Interface for form data that handles mixed types
interface SchoolFormData {
  id?: string;
  name: string;
  type: string;
  city: string;
  province: string;
  website: string;
  image?: string;
  socialMedia: { platform: string; url: string; }[];
  description: string;
  acceptanceRate: number;
  enrollment: number;
  programs: string[];
  athletics: { sport: string; gender: 'Male' | 'Female' | 'Both' }[];
  events: string[];
  scholarships: string[];
  admissionRequirements: string | string[]; // This allows both string and array
  roomAndBoardLow?: number;
  roomAndBoardHigh?: number;
  resultsImage?: string;
  feedIcon?: string;
  athleticsWebsite?: string;
  admissionsMinimum?: string;
  athleticsNickname?: string;
  primaryLanguage?: string;
  SchoolPhone?: string;
  SchoolTwitterURL?: string;
  SchoolInstagramURL?: string;
  SchoolWikiURL?: string;
  SchoolYoutubeURL?: string;
  SchoolResultsImageURL?: string;
  SchoolLocationLatitude?: string;
  SchoolLocationLongitude?: string;
  SchoolAdmissionsACT?: string;
  SchoolAdmissionsGPA?: string;
  SchoolAdmissionsSAT?: string;
}

const AddEditSchools: React.FC = () => {
  const { schools, loading, refreshSchools } = useSchools();
  
  // Country selection state
  const [selectedCountry, setSelectedCountry] = useState<'Canada' | 'US'>('Canada');
  
  // US schools state
  const [usSchools, setUsSchools] = useState<School[]>([]);
  const [usSchoolsLoading, setUsSchoolsLoading] = useState(false);
  
  // AI Draft mode state
  const [usMode, setUsMode] = useState<'Edit Existing' | 'Review AI Drafts'>('Edit Existing');
  const [aiDraftSchools, setAiDraftSchools] = useState<AIDraftSchool[]>([]);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  
  // Canadian mode state
  const [canadianMode, setCanadianMode] = useState<'Edit Existing' | 'Review AI Drafts'>('Edit Existing');
  const [canadianAIDraftSchools, setCanadianAIDraftSchools] = useState<CanadianAIDraftSchool[]>([]);
  const [canadianAIDraftLoading, setCanadianAIDraftLoading] = useState(false);
  
  // School name input for AI draft lookup
  const [schoolNameInput, setSchoolNameInput] = useState('');
  const [schoolNameLoading, setSchoolNameLoading] = useState(false);
  
  // Save operation state
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Ref for School Name field to redirect focus
  const schoolNameRef = useRef<any>(null);
  // State to control Select open/close
  const [selectOpen, setSelectOpen] = useState(false);
  // State to control map modal
  const [mapModalOpen, setMapModalOpen] = useState(false);
  
  // Sports state management
  const [sports, setSports] = useState<Array<{
    id?: string;
    AthleticsSport: string;
    AthleticsGender: string;
    AthleticsSchoolName: string;
  }>>([]);
  const [originalSports, setOriginalSports] = useState<Array<{
    id?: string;
    AthleticsSport: string;
    AthleticsGender: string;
    AthleticsSchoolName: string;
  }>>([]);
  const [sportsLoading, setSportsLoading] = useState(false);
  const [sportsSaving, setSportsSaving] = useState(false);
  
  // Athletics draft state management
  const [athleticsDraftSchools, setAthleticsDraftSchools] = useState<AthleticsDraftSchool[]>([]);
  const [athleticsDraftLoading, setAthleticsDraftLoading] = useState(false);
  const [selectedAthleticsDraftSchool, setSelectedAthleticsDraftSchool] = useState<string>('');

  // Programs state management
  const [programs, setPrograms] = useState<Array<{
    id?: string;
    ProgramName: string;
    ProgramCategory: string;
    ProgramSchoolName: string;
    ProgramCode?: string;
    ProgramDegree?: string;
    ProgramDescription?: string;
    ProgramDomesticTuition?: string;
    ProgramInternationalTuition?: string;
    ProgramPrerequisites?: string;
    ProgramLanguage?: string;
    ProgramProvinceState?: string;
    ProgramURL?: string;
    ProgramAdditionalRequirements?: string;
    ProgramAdmissionsMinimum?: string;
    ProgramExperientialLearning?: string;
    ProgramLastUpdated?: string;
    ProgramLastDeleted?: string;
    isInFirebase?: boolean; // Track if this program exists in Firebase
  }>>([]);
  const [originalPrograms, setOriginalPrograms] = useState<Array<{
    id?: string;
    ProgramName: string;
    ProgramCategory: string;
    ProgramSchoolName: string;
    ProgramCode?: string;
    ProgramDegree?: string;
    ProgramDescription?: string;
    ProgramDomesticTuition?: string;
    ProgramInternationalTuition?: string;
    ProgramPrerequisites?: string;
    ProgramLanguage?: string;
    ProgramProvinceState?: string;
    ProgramURL?: string;
    ProgramAdditionalRequirements?: string;
    ProgramAdmissionsMinimum?: string;
    ProgramExperientialLearning?: string;
    ProgramLastUpdated?: string;
    ProgramLastDeleted?: string;
    isInFirebase?: boolean;
  }>>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsSaving, setProgramsSaving] = useState(false);

  // Programs draft state management
  const [programsDraftSchools, setProgramsDraftSchools] = useState<ProgramsDraftSchool[]>([]);
  const [programsDraftLoading, setProgramsDraftLoading] = useState(false);
  const [selectedProgramsDraftSchool, setSelectedProgramsDraftSchool] = useState<string>('');

  // Program modal state management
  const [programModalOpen, setProgramModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<{
    id?: string;
    ProgramName: string;
    ProgramCategory: string;
    ProgramSchoolName: string;
    ProgramCode?: string;
    ProgramDegree?: string;
    ProgramDescription?: string;
    ProgramDomesticTuition?: string;
    ProgramInternationalTuition?: string;
    ProgramPrerequisites?: string;
    ProgramLanguage?: string;
    ProgramProvinceState?: string;
    ProgramURL?: string;
    ProgramAdditionalRequirements?: string;
    ProgramAdmissionsMinimum?: string;
    ProgramExperientialLearning?: string;
    ProgramLastUpdated?: string;
    ProgramLastDeleted?: string;
    isInFirebase?: boolean;
  } | null>(null);
  const [programModalSaving, setProgramModalSaving] = useState(false);

  // Program categories state - will be fetched from database
  const [programCategories, setProgramCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Tab state management
  const [activeTab, setActiveTab] = useState(0);
  
  // Form state - initialize with empty values
  const [formData, setFormData] = useState<SchoolFormData>({
    name: '',
    type: 'University',
    city: '',
    province: '',
    website: '',
    image: '',
    description: '',
    acceptanceRate: 0,
    enrollment: 0,
    roomAndBoardLow: 0,
    roomAndBoardHigh: 0,
    resultsImage: '',
    feedIcon: '',
    athleticsWebsite: '',
    admissionsMinimum: '',
    athleticsNickname: '',
    primaryLanguage: '',
    SchoolPhone: '',
    SchoolTwitterURL: '',
    SchoolInstagramURL: '',
    SchoolWikiURL: '',
    SchoolYoutubeURL: '',
    SchoolResultsImageURL: '',
    SchoolLocationLatitude: '',
    SchoolLocationLongitude: '',
    SchoolAdmissionsACT: '',
    SchoolAdmissionsGPA: '',
    SchoolAdmissionsSAT: '',
    programs: [],
    athletics: [],
    events: [],
    scholarships: [],
    admissionRequirements: '',
    socialMedia: []
  });

  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
  const [isNewSchool, setIsNewSchool] = useState<boolean>(false);

  // Function to fetch AI draft schools
  const fetchAIDraftSchools = async () => {
    try {
      setAiDraftLoading(true);
      const drafts = await AIDraftService.getAIDraftSchools();
      setAiDraftSchools(drafts);
    } catch (error) {
      console.error('Error fetching AI draft schools:', error);
      setAiDraftSchools([]);
    } finally {
      setAiDraftLoading(false);
    }
  };

  // Function to fetch Canadian AI draft schools
  const fetchCanadianAIDraftSchools = async () => {
    try {
      setCanadianAIDraftLoading(true);
      const drafts = await CanadianAIDraftService.getCanadianAIDraftSchools();
      setCanadianAIDraftSchools(drafts);
    } catch (error) {
      console.error('Error fetching Canadian AI draft schools:', error);
      setCanadianAIDraftSchools([]);
    } finally {
      setCanadianAIDraftLoading(false);
    }
  };

  // Function to fetch athletics draft schools
  const fetchAthleticsDraftSchools = async () => {
    try {
      setAthleticsDraftLoading(true);
      const drafts = await AthleticsDraftService.getAthleticsDraftSchools(selectedCountry);
      setAthleticsDraftSchools(drafts);
    } catch (error) {
      console.error('Error fetching athletics draft schools:', error);
      setAthleticsDraftSchools([]);
    } finally {
      setAthleticsDraftLoading(false);
    }
  };

  // Function to fetch programs draft schools
  const fetchProgramsDraftSchools = async () => {
    try {
      setProgramsDraftLoading(true);
      const drafts = await ProgramsDraftService.getProgramsDraftSchools(selectedCountry);
      setProgramsDraftSchools(drafts);
    } catch (error) {
      console.error('Error fetching programs draft schools:', error);
      setProgramsDraftSchools([]);
    } finally {
      setProgramsDraftLoading(false);
    }
  };

  // Function to fetch program categories from database
  const fetchProgramCategories = async () => {
    try {
      setCategoriesLoading(true);
      const db = getDatabase();
      const catRef = ref(db, 'programCategory');
      const catSnap = await get(catRef);
      
      if (catSnap.exists()) {
        const catData = catSnap.val();
        const categories = Object.keys(catData).map(id => catData[id].name || id);
        setProgramCategories(categories.sort());
      } else {
        setProgramCategories([]);
      }
    } catch (error) {
      console.error('Error fetching program categories:', error);
      setProgramCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Function to fetch US schools
  const fetchUSSchools = async () => {
    try {
      setUsSchoolsLoading(true);
      
      const db = getDatabase();
      const schoolsUSSnapshot = await get(ref(db, 'schools_US'));
      
      if (schoolsUSSnapshot.exists()) {
        const schoolsUSData = schoolsUSSnapshot.val();
        
        const schoolsArray: School[] = Object.entries(schoolsUSData)
          .filter(([id, data]: [string, any]) => {
            // Filter out schools that have been archived
            return !data.SchoolLastDeleted;
          })
          .map(([id, data]: [string, any]) => {
            const school = {
              id,
              name: data.SchoolName || '',
              city: data.SchoolCity || '',
              province: data.SchoolProvinceState || '',
              type: data.SchoolType || 'University',
              enrollment: data.SchoolFTUGCount || 0,
              acceptanceRate: data.acceptanceRate || 0,
              tuition: data.tuition || 0,
              roomAndBoardLow: data.SchoolRoomAndBoardLow || 0,
              roomAndBoardHigh: data.SchoolRoomAndBoardHigh || 0,
              description: data.SchoolDescription || '',
              resultsImage: data.SchoolResultsImageURL || '',
              programs: data.programs || [],
              scholarships: data.scholarships || [],
              events: data.events || [],
              website: data.SchoolWebsite || '',
              image: data.SchoolProfileImageURL || '',
              socialMedia: [],
              athletics: [],
              admissionRequirements: Array.isArray(data.SchoolBasicRequirements) 
                ? data.SchoolBasicRequirements 
                : (data.SchoolBasicRequirements ? [data.SchoolBasicRequirements] : []),
              feedIcon: data.feedIcon || '',
              athleticsNickname: data.AthleticsNickname || '',
              athleticsWebsite: data.AthleticsWebsite || '',
              SchoolWikiURL: data.SchoolWikiURL || '',
              SchoolYoutubeURL: data.SchoolYoutubeURL || '',
              SchoolLastDeleted: data.SchoolLastDeleted || undefined,
              SchoolPhone: data.SchoolPhone || '',
              SchoolTwitterURL: data.SchoolTwitterURL || '',
              SchoolInstagramURL: data.SchoolInstagramURL || '',
              SchoolResultsImageURL: data.SchoolResultsImageURL || '',
              SchoolLocationLatitude: data.SchoolLocationLatitude || '',
              SchoolLocationLongitude: data.SchoolLocationLongitude || '',
              SchoolAdmissionsACT: data.SchoolAdmissionsACT || '',
              SchoolAdmissionsGPA: data.SchoolAdmissionsGPA || '',
              SchoolAdmissionsSAT: data.SchoolAdmissionsSAT || '',
              primaryLanguage: data.primaryLanguage || '',
              admissionsMinimum: data.SchoolAdmissionsMin || '',
              ...data
            };
            return school;
          });
        
        setUsSchools(schoolsArray);
      } else {
        setUsSchools([]);
      }
    } catch (error) {
      console.error('Error fetching US schools:', error);
      setUsSchools([]);
    } finally {
      setUsSchoolsLoading(false);
    }
  };

  // Load schools when component mounts or country/mode changes
  useEffect(() => {
    if (selectedCountry === 'US') {
      if (usMode === 'Edit Existing') {
        fetchUSSchools();
      } else {
        fetchAIDraftSchools();
      }
    } else if (selectedCountry === 'Canada') {
      if (canadianMode === 'Review AI Drafts') {
        fetchCanadianAIDraftSchools();
      }
    }
  }, [selectedCountry, usMode, canadianMode]);

  // Load sports when a school is selected
  useEffect(() => {
    if (formData.name) {
      loadSports(formData.name);
    } else {
      setSports([]);
      setOriginalSports([]);
    }
  }, [formData.name, selectedCountry]);

  // Load programs when a school is selected
  useEffect(() => {
    if (formData.name) {
      loadPrograms(formData.name);
    } else {
      setPrograms([]);
      setOriginalPrograms([]);
    }
  }, [formData.name, selectedCountry]);

  // Load athletics draft schools when country changes
  useEffect(() => {
    fetchAthleticsDraftSchools();
  }, [selectedCountry]);

  // Load programs draft schools when country changes
  useEffect(() => {
    fetchProgramsDraftSchools();
  }, [selectedCountry]);

  // Load program categories when component mounts
  useEffect(() => {
    fetchProgramCategories();
  }, []);

  const handleInputChange = (field: keyof School, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCountryChange = (
    event: React.MouseEvent<HTMLElement>,
    newCountry: 'Canada' | 'US' | null,
  ) => {
    if (newCountry !== null) {
      setSelectedCountry(newCountry);
      // Clear form and selection when switching countries
      handleCancel();
      // Reset US mode to default when switching to US
      if (newCountry === 'US') {
        setUsMode('Edit Existing');
      }
    }
  };

  const handleUSModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'Edit Existing' | 'Review AI Drafts' | null,
  ) => {
    if (newMode !== null) {
      setUsMode(newMode);
      // Clear form and selection when switching modes
      handleCancel();
    }
  };

  const handleCanadianModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'Edit Existing' | 'Review AI Drafts' | null,
  ) => {
    if (newMode !== null) {
      setCanadianMode(newMode);
      // Clear form and selection when switching modes
      handleCancel();
    }
  };

  // Handle school name input lookup
  const handleSchoolNameLookup = async () => {
    if (!schoolNameInput.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a school name' });
      return;
    }

    setSchoolNameLoading(true);
    try {
      // Get all AI draft schools
      const drafts = await AIDraftService.getAIDraftSchools();
      
      // Find matching school by name (case-insensitive)
      const matchingDraft = drafts.find(draft => 
        draft.name.toLowerCase().includes(schoolNameInput.toLowerCase()) ||
        schoolNameInput.toLowerCase().includes(draft.name.toLowerCase())
      );

      if (matchingDraft) {
        // Populate form with the matching draft data
        const formData = AIDraftService.convertAIDraftToFormData(matchingDraft.data);
        setFormData(formData);
        setSelectedSchoolId(matchingDraft.id);
        setIsNewSchool(true);
        
        setSaveMessage({ 
          type: 'success', 
          text: `Found and loaded data for ${matchingDraft.name}` 
        });
      } else {
        setSaveMessage({ 
          type: 'error', 
          text: `No AI draft found for "${schoolNameInput}". Available schools: ${drafts.map(d => d.name).join(', ')}` 
        });
      }
    } catch (error) {
      console.error('Error looking up school:', error);
      setSaveMessage({ 
        type: 'error', 
        text: `Error looking up school: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setSchoolNameLoading(false);
    }
  };

  const handleSchoolSelect = async (schoolId: string) => {
    try {
      setSelectedSchoolId(schoolId);
      setActiveTab(0); // Reset to first tab when selecting a school
    
    if (schoolId === 'new') {
      // Handle "Add a new school" selection
      setIsNewSchool(true);
      setFormData({
        name: '',
        type: 'University',
        city: '',
        province: '',
        website: '',
        image: '',
        description: '',
        acceptanceRate: 0,
        enrollment: 0,
        roomAndBoardLow: 0,
        roomAndBoardHigh: 0,
        resultsImage: '',
        feedIcon: '',
        athleticsWebsite: '',
        admissionsMinimum: '',
        athleticsNickname: '',
        primaryLanguage: '',
        SchoolPhone: '',
        SchoolTwitterURL: '',
        SchoolInstagramURL: '',
        SchoolWikiURL: '',
        SchoolYoutubeURL: '',
        SchoolResultsImageURL: '',
        SchoolLocationLatitude: '',
        SchoolLocationLongitude: '',
        SchoolAdmissionsACT: '',
        SchoolAdmissionsGPA: '',
        SchoolAdmissionsSAT: '',
        programs: [],
        athletics: [],
        events: [],
        scholarships: [],
        admissionRequirements: [],
        socialMedia: []
      });
    } else if (schoolId) {
      // Handle school selection based on mode
      if (selectedCountry === 'US' && usMode === 'Review AI Drafts') {
        // Handle US AI draft school selection
        setIsNewSchool(true); // AI drafts are treated as new schools
        const selectedDraft = aiDraftSchools.find(draft => draft.id === schoolId);
        
        if (selectedDraft) {
          const formData = AIDraftService.convertAIDraftToFormData(selectedDraft.data);
          setFormData(formData);
        }
      } else if (selectedCountry === 'Canada' && canadianMode === 'Review AI Drafts') {
        // Handle Canadian AI draft school selection
        setIsNewSchool(true); // AI drafts are treated as new schools
        const selectedDraft = canadianAIDraftSchools.find(draft => draft.id === schoolId);
        
        if (selectedDraft) {
          // Fetch the actual data for this draft
          const draftData = await CanadianAIDraftService.getCanadianAIDraftSchoolData(selectedDraft.fileName);
          if (draftData) {
            const formData = CanadianAIDraftService.convertCanadianAIDraftToFormData(draftData);
            setFormData(formData);
          }
        }
      } else {
        // Handle existing school selection
        setIsNewSchool(false);
        const currentSchools = selectedCountry === 'Canada' ? schools : usSchools;
        
        const selectedSchool = currentSchools.find(school => school.id === schoolId);
        
        if (selectedSchool) {
          const formData = {
            name: selectedSchool.name || '',
            type: selectedSchool.type || 'University',
            city: selectedSchool.city || '',
            province: selectedSchool.province || '',
            website: selectedSchool.website || '',
            image: selectedSchool.image || '',
            description: selectedSchool.description || '',
            acceptanceRate: selectedSchool.acceptanceRate || 0,
            enrollment: selectedSchool.enrollment || 0,
            roomAndBoardLow: selectedSchool.roomAndBoardLow || 0,
            roomAndBoardHigh: selectedSchool.roomAndBoardHigh || 0,
            resultsImage: selectedSchool.resultsImage || '',
            feedIcon: selectedSchool.feedIcon || '',
            athleticsWebsite: selectedSchool.athleticsWebsite || '',
            admissionsMinimum: selectedSchool.admissionsMinimum || '',
            athleticsNickname: selectedSchool.athleticsNickname || '',
            primaryLanguage: selectedSchool.primaryLanguage || '',
            SchoolPhone: selectedSchool.SchoolPhone || '',
            SchoolTwitterURL: selectedSchool.SchoolTwitterURL || '',
            SchoolInstagramURL: selectedSchool.SchoolInstagramURL || '',
            SchoolWikiURL: selectedSchool.SchoolWikiURL || '',
            SchoolYoutubeURL: selectedSchool.SchoolYoutubeURL || '',
            SchoolResultsImageURL: selectedSchool.SchoolResultsImageURL || '',
            SchoolLocationLatitude: selectedSchool.SchoolLocationLatitude || '',
            SchoolLocationLongitude: selectedSchool.SchoolLocationLongitude || '',
            SchoolAdmissionsACT: selectedSchool.SchoolAdmissionsACT || '',
            SchoolAdmissionsGPA: selectedSchool.SchoolAdmissionsGPA || '',
            SchoolAdmissionsSAT: selectedSchool.SchoolAdmissionsSAT || '',
            programs: selectedSchool.programs || [],
            athletics: selectedSchool.athletics || [],
            events: selectedSchool.events || [],
            scholarships: selectedSchool.scholarships || [],
            admissionRequirements: Array.isArray(selectedSchool.admissionRequirements) 
              ? selectedSchool.admissionRequirements 
              : (selectedSchool.admissionRequirements ? [selectedSchool.admissionRequirements] : []),
            socialMedia: selectedSchool.socialMedia || []
          };
          setFormData(formData);
        }
      }
    } else {
      // Reset form when "Select a school" is chosen
      setIsNewSchool(false);
      handleCancel();
    }
    } catch (error) {
      console.error('❌ Error in handleSchoolSelect:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      setSaveMessage({ type: 'error', text: 'School name is required' });
      return;
    }

    setSaving(true);
    
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'schools_US' : 'schools';
      
      // Convert all form data to strings for Firebase storage
      const schoolData: any = {
        SchoolName: formData.name?.toString() || '',
        SchoolType: formData.type?.toString() || 'University',
        SchoolCity: formData.city?.toString() || '',
        SchoolProvinceState: formData.province?.toString() || '',
        SchoolWebsite: formData.website?.toString() || '',
        SchoolProfileImageURL: formData.image?.toString() || '',
        SchoolDescription: formData.description?.toString() || '',
        SchoolAdmissionsMin: formData.admissionsMinimum?.toString() || '0',
        acceptanceRate: formData.acceptanceRate?.toString() || '0',
        SchoolFTUGCount: formData.enrollment?.toString() || '0',
        SchoolRoomAndBoardLow: formData.roomAndBoardLow?.toString() || '0',
        SchoolRoomAndBoardHigh: formData.roomAndBoardHigh?.toString() || '0',
        SchoolResultsImageURL: formData.resultsImage?.toString() || '',
        AthleticsNickname: formData.athleticsNickname?.toString() || '',
        AthleticsWebsite: formData.athleticsWebsite?.toString() || '',
        SchoolBasicRequirements: Array.isArray(formData.admissionRequirements) ? formData.admissionRequirements.join(',') : (formData.admissionRequirements || ''),
        SchoolPhone: formData.SchoolPhone?.toString() || '',
        SchoolTwitterURL: formData.SchoolTwitterURL?.toString() || '',
        SchoolInstagramURL: formData.SchoolInstagramURL?.toString() || '',
        SchoolWikiURL: formData.SchoolWikiURL?.toString() || '',
        SchoolYoutubeURL: formData.SchoolYoutubeURL?.toString() || '',
        SchoolLocationLatitude: formData.SchoolLocationLatitude?.toString() || '',
        SchoolLocationLongitude: formData.SchoolLocationLongitude?.toString() || '',
        // Additional fields that might be needed
        programs: formData.programs?.join(',') || '',
        athletics: formData.athletics?.map((a: any) => `${a.sport}:${a.gender}`).join(',') || '',
        events: formData.events?.join(',') || '',
        scholarships: formData.scholarships?.join(',') || '',
        socialMedia: formData.socialMedia?.map((sm: any) => `${sm.platform}:${sm.url}`).join(',') || '',
        // Timestamp for when the record was created/updated
        lastUpdated: Date.now().toString()
      };

      // Only add US-specific admission fields for US schools
      if (selectedCountry === 'US') {
        schoolData.SchoolAdmissionsACT = formData.SchoolAdmissionsACT?.toString() || '';
        schoolData.SchoolAdmissionsGPA = formData.SchoolAdmissionsGPA?.toString() || '';
        schoolData.SchoolAdmissionsSAT = formData.SchoolAdmissionsSAT?.toString() || '';
      }

      if (isNewSchool) {
        // Create new school record
        const newSchoolRef = push(ref(db, tableName));
        await set(newSchoolRef, schoolData);
        
        setSaveMessage({ 
          type: 'success', 
          text: `New ${selectedCountry} school "${formData.name}" created successfully!` 
        });
      } else {
        // Update existing school record
        const schoolRef = ref(db, `${tableName}/${selectedSchoolId}`);
        await update(schoolRef, schoolData);
        
        setSaveMessage({ 
          type: 'success', 
          text: `${selectedCountry} school "${formData.name}" updated successfully!` 
        });
      }

      // Handle AI draft file movement if this was an AI draft
      if (selectedCountry === 'US' && usMode === 'Review AI Drafts' && isNewSchool) {
        const selectedDraft = aiDraftSchools.find(draft => draft.id === selectedSchoolId);
        if (selectedDraft) {
          await AIDraftService.moveFileToApproved(selectedDraft.fileName);
        }
      }

      // Refresh data after successful save
      if (selectedCountry === 'Canada') {
        refreshSchools();
      } else if (selectedCountry === 'US') {
        if (usMode === 'Edit Existing') {
          // Refresh US schools data to show updated values
          await fetchUSSchools();
        } else {
          // Refresh AI draft schools data
          await fetchAIDraftSchools();
        }
      }

      // Clear form after successful save
      handleCancel();
      
    } catch (error) {
      console.error('Error saving school:', error);
      setSaveMessage({ 
        type: 'error', 
        text: `Failed to save school: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setSaving(false);
    }
  };

  // Sports management functions
  const loadSports = async (schoolName: string) => {
    if (!schoolName) return;
    
    setSportsLoading(true);
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'athletics_US' : 'athletics';
      const sportsRef = ref(db, tableName);
      const snapshot = await get(sportsRef);
      
      if (snapshot.exists()) {
        const sportsData = snapshot.val();
        const schoolSports = Object.entries(sportsData)
          .filter(([id, data]: [string, any]) => data.AthleticsSchoolName === schoolName)
          .map(([id, data]: [string, any]) => ({
            id,
            AthleticsSport: data.AthleticsSport || '',
            AthleticsGender: data.AthleticsGender || '',
            AthleticsSchoolName: data.AthleticsSchoolName || ''
          }));
        
        setSports(schoolSports);
        setOriginalSports(schoolSports);
      } else {
        setSports([]);
        setOriginalSports([]);
      }
    } catch (error) {
      console.error('Error loading sports:', error);
      setSports([]);
      setOriginalSports([]);
    } finally {
      setSportsLoading(false);
    }
  };

  const saveSports = async () => {
    if (!formData.name) return;
    
    setSportsSaving(true);
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'athletics_US' : 'athletics';
      
      // Get current sports from Firebase
      const sportsRef = ref(db, tableName);
      const snapshot = await get(sportsRef);
      const currentSports = snapshot.exists() ? snapshot.val() : {};
      
      // Remove existing sports for this school
      const updatedSports = { ...currentSports };
      Object.keys(updatedSports).forEach(key => {
        if (updatedSports[key].AthleticsSchoolName === formData.name) {
          delete updatedSports[key];
        }
      });
      
      // Add new sports
      sports.forEach(sport => {
        if (sport.AthleticsSport.trim()) {
          const newSportRef = push(ref(db, tableName));
          updatedSports[newSportRef.key!] = {
            AthleticsSport: sport.AthleticsSport,
            AthleticsGender: sport.AthleticsGender,
            AthleticsSchoolName: formData.name
          };
        }
      });
      
      // Save to Firebase
      await set(sportsRef, updatedSports);
      
      // Update original sports to match current state
      setOriginalSports([...sports]);
      
      setSaveMessage({ type: 'success', text: 'Sports saved successfully!' });
    } catch (error) {
      console.error('Error saving sports:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save sports. Please try again.' });
    } finally {
      setSportsSaving(false);
    }
  };

  const cancelSportsChanges = () => {
    setSports([...originalSports]);
  };

  const addSport = () => {
    setSports([...sports, {
      AthleticsSport: '',
      AthleticsGender: 'M',
      AthleticsSchoolName: formData.name
    }]);
  };

  const updateSport = (index: number, field: string, value: string) => {
    const updatedSports = [...sports];
    updatedSports[index] = { ...updatedSports[index], [field]: value };
    setSports(updatedSports);
  };

  const removeSport = (index: number) => {
    const updatedSports = sports.filter((_, i) => i !== index);
    setSports(updatedSports);
  };

  const importAthleticsDraft = () => {
    if (!selectedAthleticsDraftSchool) return;
    
    const selectedDraft = athleticsDraftSchools.find(draft => draft.id === selectedAthleticsDraftSchool);
    if (!selectedDraft) return;
    
    // Add the draft sports to the current sports list
    const newSports = selectedDraft.sports.map(sport => ({
      AthleticsSport: sport.AthleticsSport,
      AthleticsGender: sport.AthleticsGender,
      AthleticsSchoolName: formData.name
    }));
    
    setSports([...sports, ...newSports]);
    setSelectedAthleticsDraftSchool('');
  };

  // Programs management functions
  const loadPrograms = async (schoolName: string) => {
    if (!schoolName) return;
    
    setProgramsLoading(true);
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'programs_US' : 'programs';
      const programsRef = ref(db, tableName);
      const snapshot = await get(programsRef);
      
      if (snapshot.exists()) {
        const programsData = snapshot.val();
        const schoolPrograms = Object.entries(programsData)
          .filter(([id, data]: [string, any]) => data.ProgramSchoolName === schoolName)
          .map(([id, data]: [string, any]) => ({
            id,
            ProgramName: data.ProgramName || '',
            ProgramCategory: data.ProgramCategory || '',
            ProgramSchoolName: data.ProgramSchoolName || '',
            ProgramSchoolID: data.ProgramSchoolID || '',
            ProgramCode: data.ProgramCode || '',
            ProgramDegree: data.ProgramDegree || '',
            ProgramDescription: data.ProgramDescription || '',
            ProgramDomesticTuition: data.ProgramDomesticTuition || '',
            ProgramInternationalTuition: data.ProgramInternationalTuition || '',
            ProgramPrerequisites: data.ProgramPrerequisites || '',
            ProgramLanguage: data.ProgramLanguage || '',
            ProgramProvinceState: data.ProgramProvinceState || '',
            ProgramURL: data.ProgramURL || '',
            ProgramAdditionalRequirements: data.ProgramAdditionalRequirements || '',
            ProgramAdmissionsMinimum: data.ProgramAdmissionsMinimum || '',
            ProgramExperientialLearning: data.ProgramExperientialLearning || '',
            ProgramLastUpdated: data.ProgramLastUpdated || '',
            ProgramLastDeleted: data.ProgramLastDeleted || '',
            isInFirebase: true
          }));
        
        setPrograms(schoolPrograms);
        setOriginalPrograms(schoolPrograms);
      } else {
        setPrograms([]);
        setOriginalPrograms([]);
      }
    } catch (error) {
      console.error('Error loading programs:', error);
      setPrograms([]);
      setOriginalPrograms([]);
    } finally {
      setProgramsLoading(false);
    }
  };

  const savePrograms = async () => {
    if (!formData.name) return;
    
    setProgramsSaving(true);
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'programs_US' : 'programs';
      
      // Get current programs from Firebase
      const programsRef = ref(db, tableName);
      const snapshot = await get(programsRef);
      const currentPrograms = snapshot.exists() ? snapshot.val() : {};
      
      // Remove existing programs for this school
      const updatedPrograms = { ...currentPrograms };
      Object.keys(updatedPrograms).forEach(key => {
        if (updatedPrograms[key].ProgramSchoolName === formData.name) {
          delete updatedPrograms[key];
        }
      });
      
      // Add new programs
      programs.forEach(program => {
        if (program.ProgramName.trim()) {
          const newProgramRef = push(ref(db, tableName));
          updatedPrograms[newProgramRef.key!] = {
            ProgramName: program.ProgramName,
            ProgramCategory: program.ProgramCategory,
            ProgramSchoolName: formData.name,
            ProgramCode: program.ProgramCode || '',
            ProgramDegree: program.ProgramDegree || '',
            ProgramDescription: program.ProgramDescription || '',
            ProgramDomesticTuition: program.ProgramDomesticTuition || '',
            ProgramInternationalTuition: program.ProgramInternationalTuition || '',
            ProgramPrerequisites: program.ProgramPrerequisites || '',
            ProgramLanguage: program.ProgramLanguage || '',
            ProgramProvinceState: program.ProgramProvinceState || '',
            ProgramURL: program.ProgramURL || '',
            ProgramAdditionalRequirements: program.ProgramAdditionalRequirements || '',
            ProgramAdmissionsMinimum: program.ProgramAdmissionsMinimum || '',
            ProgramExperientialLearning: program.ProgramExperientialLearning || '',
            ProgramLastUpdated: new Date().toISOString().replace('T', ' ').substring(0, 19),
            ProgramLastDeleted: ''
          };
        }
      });
      
      // Save to Firebase
      await set(programsRef, updatedPrograms);
      
      // Update original programs to match current state
      setOriginalPrograms([...programs]);
      
      setSaveMessage({ type: 'success', text: 'Programs saved successfully!' });
    } catch (error) {
      console.error('Error saving programs:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save programs. Please try again.' });
    } finally {
      setProgramsSaving(false);
    }
  };

  const cancelProgramsChanges = () => {
    setPrograms([...originalPrograms]);
  };

  const addProgram = () => {
    const newProgram = {
      ProgramName: '',
      ProgramCategory: '',
      ProgramSchoolName: formData.name,
      ProgramCode: '',
      ProgramDegree: '',
      ProgramDescription: '',
      ProgramDomesticTuition: '',
      ProgramInternationalTuition: '',
      ProgramPrerequisites: '',
      ProgramLanguage: 'English',
      ProgramProvinceState: selectedCountry === 'Canada' ? 'British Columbia' : '',
      ProgramURL: '',
      ProgramAdditionalRequirements: '',
      ProgramAdmissionsMinimum: '',
      ProgramExperientialLearning: '',
      ProgramLastUpdated: '',
      ProgramLastDeleted: '',
      isInFirebase: false
    };
    
    setPrograms([...programs, newProgram]);
    
    // Automatically open the modal for the new program
    setEditingProgram(newProgram);
    setProgramModalOpen(true);
  };

  const updateProgram = (index: number, field: string, value: string) => {
    const updatedPrograms = [...programs];
    updatedPrograms[index] = { ...updatedPrograms[index], [field]: value };
    setPrograms(updatedPrograms);
  };

  const removeProgram = (index: number) => {
    const updatedPrograms = programs.filter((_, i) => i !== index);
    setPrograms(updatedPrograms);
  };

  const importProgramsDraft = () => {
    if (!selectedProgramsDraftSchool) return;
    
    const selectedDraft = programsDraftSchools.find(draft => draft.id === selectedProgramsDraftSchool);
    if (!selectedDraft) return;
    
    // Add the draft programs to the current programs list
    const newPrograms = selectedDraft.programs.map(program => ({
      ProgramName: program.ProgramName,
      ProgramCategory: program.ProgramCategory,
      ProgramSchoolName: formData.name,
      ProgramCode: program.ProgramCode || '',
      ProgramDegree: program.ProgramDegree || '',
      ProgramDescription: program.ProgramDescription || '',
      ProgramDomesticTuition: program.ProgramDomesticTuition || '',
      ProgramInternationalTuition: program.ProgramInternationalTuition || '',
      ProgramPrerequisites: program.ProgramPrerequisites || '',
      ProgramLanguage: program.ProgramLanguage || 'English',
      ProgramProvinceState: program.ProgramProvinceState || (selectedCountry === 'Canada' ? 'British Columbia' : ''),
      ProgramURL: program.ProgramURL || '',
      ProgramAdditionalRequirements: program.ProgramAdditionalRequirements || '',
      ProgramAdmissionsMinimum: program.ProgramAdmissionsMinimum || '',
      ProgramExperientialLearning: program.ProgramExperientialLearning || '',
      ProgramLastUpdated: program.ProgramLastUpdated || '',
      ProgramLastDeleted: program.ProgramLastDeleted || '',
      isInFirebase: false
    }));
    
    setPrograms([...programs, ...newPrograms]);
    setSelectedProgramsDraftSchool('');
  };

  // Program modal functions
  const openProgramModal = (program: any) => {
    setEditingProgram({ ...program });
    setProgramModalOpen(true);
  };

  const closeProgramModal = () => {
    setProgramModalOpen(false);
    setEditingProgram(null);
  };

  const handleProgramModalInputChange = (field: string, value: string) => {
    if (editingProgram) {
      setEditingProgram({ ...editingProgram, [field]: value });
    }
  };

  const saveProgramModal = async () => {
    if (!editingProgram || !formData.name) return;
    
    setProgramModalSaving(true);
    try {
      const db = getDatabase();
      const tableName = selectedCountry === 'US' ? 'programs_US' : 'programs';
      
      console.log('Saving program to table:', tableName);
      console.log('School name:', formData.name);
      console.log('School ID:', formData.id);
      console.log('Editing program:', editingProgram);
      
      const programData = {
        ProgramName: editingProgram.ProgramName,
        ProgramCategory: editingProgram.ProgramCategory,
        ProgramSchoolName: formData.name,
        ProgramSchoolID: formData.id || formData.name.replace(/\s+/g, '-'),
        ProgramCode: editingProgram.ProgramCode || '',
        ProgramDegree: editingProgram.ProgramDegree || '',
        ProgramDescription: editingProgram.ProgramDescription || '',
        ProgramDomesticTuition: editingProgram.ProgramDomesticTuition || '',
        ProgramInternationalTuition: editingProgram.ProgramInternationalTuition || '',
        ProgramPrerequisites: editingProgram.ProgramPrerequisites || '',
        ProgramLanguage: editingProgram.ProgramLanguage || 'English',
        ProgramProvinceState: editingProgram.ProgramProvinceState || (selectedCountry === 'Canada' ? 'British Columbia' : ''),
        ProgramURL: editingProgram.ProgramURL || '',
        ProgramAdditionalRequirements: editingProgram.ProgramAdditionalRequirements || '',
        ProgramAdmissionsMinimum: editingProgram.ProgramAdmissionsMinimum || '',
        ProgramExperientialLearning: editingProgram.ProgramExperientialLearning || '',
        ProgramLastUpdated: new Date().toISOString().replace('T', ' ').substring(0, 19),
        ProgramLastDeleted: ''
      };

      let newProgramId = editingProgram.id;
      
      // Check if this is an existing program that's already in Firebase
      if (editingProgram.id && editingProgram.isInFirebase) {
        // Update existing program
        console.log('Updating existing program with ID:', editingProgram.id);
        const programRef = ref(db, `${tableName}/${editingProgram.id}`);
        await update(programRef, programData);
      } else {
        // Create new program (either completely new or local program being saved for first time)
        console.log('Creating new program');
        const newProgramRef = push(ref(db, tableName));
        await set(newProgramRef, programData);
        newProgramId = newProgramRef.key || undefined; // Get the new ID from Firebase
        console.log('New program created with ID:', newProgramId);
      }

      // Update the programs list - find the program by matching all key fields since ID might change
      const updatedPrograms = programs.map(p => {
        // Match by ProgramName and ProgramSchoolName to find the right program
        if (p.ProgramName === editingProgram.ProgramName && 
            p.ProgramSchoolName === editingProgram.ProgramSchoolName &&
            p.ProgramCategory === editingProgram.ProgramCategory) {
          return { ...editingProgram, isInFirebase: true, id: newProgramId };
        }
        return p;
      });
      
      setPrograms(updatedPrograms);
      setOriginalPrograms(updatedPrograms);

      setSaveMessage({ type: 'success', text: 'Program saved successfully!' });
      closeProgramModal();
    } catch (error) {
      console.error('Error saving program:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save program. Please try again.' });
    } finally {
      setProgramModalSaving(false);
    }
  };

  const handleCancel = () => {
    // Clear the form and deselect the school, but don't affect the original data
    setActiveTab(0); // Reset to first tab when canceling
    setFormData({
      name: '',
      type: 'University',
      city: '',
      province: '',
      website: '',
      image: '',
      description: '',
      acceptanceRate: 0,
      enrollment: 0,
      roomAndBoardLow: 0,
      roomAndBoardHigh: 0,
      resultsImage: '',
      feedIcon: '',
      athleticsWebsite: '',
      admissionsMinimum: '',
      athleticsNickname: '',
      primaryLanguage: '',
      SchoolPhone: '',
      SchoolTwitterURL: '',
      SchoolInstagramURL: '',
      SchoolWikiURL: '',
      SchoolYoutubeURL: '',
      SchoolResultsImageURL: '',
      SchoolLocationLatitude: '',
      SchoolLocationLongitude: '',
      programs: [],
      athletics: [],
      events: [],
      scholarships: [],
      admissionRequirements: [],
      socialMedia: []
    });
    setSelectedSchoolId('');
    setIsNewSchool(false);
    setSchoolNameInput(''); // Clear the school name input
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Add / Edit School
        </Typography>
        
        {/* Country Selection Toggle */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Country Selection
          </Typography>
          <ToggleButtonGroup
            value={selectedCountry}
            exclusive
            onChange={handleCountryChange}
            aria-label="country selection"
            size="large"
          >
            <ToggleButton value="Canada" aria-label="canada">
              Canada
            </ToggleButton>
            <ToggleButton value="US" aria-label="us">
              US
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Schools will be saved to the {selectedCountry === 'US' ? 'schools_US' : 'schools'} table in Firebase
          </Typography>
        </Box>

        {/* US Mode Selection Toggle - Only show when US is selected */}
        {selectedCountry === 'US' && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              US School Management Mode
            </Typography>
            <ToggleButtonGroup
              value={usMode}
              exclusive
              onChange={handleUSModeChange}
              aria-label="US mode selection"
              size="large"
            >
              <ToggleButton value="Edit Existing" aria-label="edit-existing">
                Edit Existing
              </ToggleButton>
              <ToggleButton value="Review AI Drafts" aria-label="review-ai-drafts">
                Review AI Drafts
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {usMode === 'Edit Existing' 
                ? 'Edit existing US schools or add new ones manually' 
                : 'Review and approve AI-generated school data from the schoolsToBeReviewed folder'
              }
            </Typography>
          </Box>
        )}

        {/* Canadian Mode Selection Toggle - Only show when Canada is selected */}
        {selectedCountry === 'Canada' && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Canadian School Management Mode
            </Typography>
            <ToggleButtonGroup
              value={canadianMode}
              exclusive
              onChange={handleCanadianModeChange}
              aria-label="Canadian mode selection"
              size="large"
            >
              <ToggleButton value="Edit Existing" aria-label="edit-existing">
                Edit Existing
              </ToggleButton>
              <ToggleButton value="Review AI Drafts" aria-label="review-ai-drafts">
                Review AI Drafts
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {canadianMode === 'Edit Existing' 
                ? 'Edit existing Canadian schools or add new ones manually' 
                : 'Review and approve AI-generated school data from the schoolsToBeReviewedCanada folder'
              }
            </Typography>
          </Box>
        )}

        {/* School Name Lookup - Only show when US and Review AI Drafts is selected */}
        {selectedCountry === 'US' && usMode === 'Review AI Drafts' && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Quick School Lookup
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                label="Enter School Name"
                value={schoolNameInput}
                onChange={(e) => setSchoolNameInput(e.target.value)}
                placeholder="e.g., Stanford University"
                helperText="Enter the name of a school to quickly load its AI draft data"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSchoolNameLookup();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleSchoolNameLookup}
                disabled={schoolNameLoading || !schoolNameInput.trim()}
                sx={{ minWidth: 120, height: 56 }}
              >
                {schoolNameLoading ? 'Loading...' : 'Load Data'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will search for and load AI draft data for the specified school name
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 4 }} />
        
        {/* School Selection Dropdown */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            {selectedCountry === 'US' && usMode === 'Review AI Drafts' 
              ? 'Select AI Draft to Review' 
              : selectedCountry === 'Canada' && canadianMode === 'Review AI Drafts'
              ? 'Select Canadian AI Draft to Review'
              : 'Select School to Edit'
            }
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Choose a school</InputLabel>
            <Select
              key={`${selectedCountry}-${usMode}-${selectedCountry === 'Canada' ? schools.length : usSchools.length}`}
              value={selectedSchoolId}
              label="Choose a school"
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              onChange={(e) => handleSchoolSelect(e.target.value)}
              disabled={
                selectedCountry === 'Canada' 
                  ? canadianMode === 'Review AI Drafts' 
                    ? canadianAIDraftLoading 
                    : loading
                  : usMode === 'Edit Existing' 
                    ? usSchoolsLoading 
                    : aiDraftLoading
              }
            >
              <MenuItem value="">
                <em>Select a school</em>
              </MenuItem>
              {selectedCountry === 'US' && usMode === 'Review AI Drafts' ? (
                // US AI Draft mode - only show US AI draft schools
                aiDraftSchools
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((draft) => (
                    <MenuItem key={draft.id} value={draft.id}>
                      {draft.name}
                    </MenuItem>
                  ))
              ) : selectedCountry === 'Canada' && canadianMode === 'Review AI Drafts' ? (
                // Canadian AI Draft mode - only show Canadian AI draft schools
                canadianAIDraftSchools
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((draft) => (
                    <MenuItem key={draft.id} value={draft.id}>
                      {draft.name}
                    </MenuItem>
                  ))
              ) : (
                // Edit Existing mode - show existing schools + add new option
                <>
                  <MenuItem 
                    value="new"
                    onClick={() => {
                      setSelectedSchoolId('new');
                      handleSchoolSelect('new');
                      setSelectOpen(false);
                      setTimeout(() => {
                        if (schoolNameRef.current) {
                          schoolNameRef.current.focus();
                        }
                      }, 100);
                    }}
                  >
                    Add a new school
                  </MenuItem>
                  {(() => {
                    const currentSchools = selectedCountry === 'Canada' ? schools : usSchools;
                    return currentSchools
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((school) => (
                        <MenuItem 
                          key={school.id} 
                          value={school.id}
                          onClick={() => {
                            setSelectedSchoolId(school.id);
                            handleSchoolSelect(school.id);
                            setSelectOpen(false);
                            setTimeout(() => {
                              if (schoolNameRef.current) {
                                schoolNameRef.current.focus();
                              }
                            }, 100);
                          }}
                        >
                          {school.name}
                        </MenuItem>
                      ));
                  })()}
                </>
              )}
            </Select>
          </FormControl>
          
          {/* Confirmation message for new school or AI draft */}
          {isNewSchool && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: selectedCountry === 'US' && usMode === 'Review AI Drafts' ? 'success.light' : 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: selectedCountry === 'US' && usMode === 'Review AI Drafts' ? 'success.main' : 'info.main'
            }}>
              <Typography variant="body2" color={selectedCountry === 'US' && usMode === 'Review AI Drafts' ? 'success.contrastText' : 'info.contrastText'}>
                {selectedCountry === 'US' && usMode === 'Review AI Drafts' ? (
                  <>
                    <strong>Reviewing AI Draft:</strong> Review and edit the AI-generated data below. Click "Save School" to approve and add this school to the schools_US table. The draft file will be moved to the schoolsApprovedAndAdded folder.
                  </>
                ) : (
                  <>
                    <strong>Creating a new school record:</strong> Fill out the form below and click "Save School" to create a new school in the {selectedCountry === 'US' ? 'schools_US' : 'schools'} table.
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />
        
        {/* Tab Navigation - Only show when a school is selected */}
        {selectedSchoolId && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="school information tabs">
              <Tab label="School Profile" id="school-tab-0" aria-controls="school-tabpanel-0" />
              <Tab label="Sports" id="school-tab-1" aria-controls="school-tabpanel-1" />
              <Tab label="Programs" id="school-tab-2" aria-controls="school-tabpanel-2" />
              <Tab label="Events" id="school-tab-3" aria-controls="school-tabpanel-3" />
            </Tabs>
          </Box>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* Tab Content */}
          <TabPanel value={activeTab} index={0}>
            {/* Basic Information Section */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Basic Information
              </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  ref={schoolNameRef}
                  fullWidth
                  label="School Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>School Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="School Type"
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <MenuItem value="University">University</MenuItem>
                    <MenuItem value="College">College</MenuItem>
                    <MenuItem value="Community College">Community College</MenuItem>
                    <MenuItem value="Technical Institute">Technical Institute</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={selectedCountry === 'Canada' ? 'Province' : 'State'}
                  value={formData.province}
                  onChange={(e) => handleInputChange('province', e.target.value)}
                  required
                />
              </Grid>

            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Website, Social Media & Contact Information */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Website, Social Media & Contact Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website URL"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.SchoolPhone}
                  onChange={(e) => handleInputChange('SchoolPhone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Twitter URL"
                  value={formData.SchoolTwitterURL}
                  onChange={(e) => handleInputChange('SchoolTwitterURL', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Instagram URL"
                  value={formData.SchoolInstagramURL}
                  onChange={(e) => handleInputChange('SchoolInstagramURL', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wiki URL"
                  value={formData.SchoolWikiURL}
                  onChange={(e) => handleInputChange('SchoolWikiURL', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="YouTube URL"
                  value={formData.SchoolYoutubeURL}
                  onChange={(e) => handleInputChange('SchoolYoutubeURL', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Profile Image URL"
                  value={formData.image}
                  onChange={(e) => handleInputChange('image', e.target.value)}
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Results Image URL"
                  value={formData.resultsImage}
                  onChange={(e) => handleInputChange('resultsImage', e.target.value)}
                  type="url"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Location Information */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                Location Information
              </Typography>
              {formData.SchoolLocationLatitude && formData.SchoolLocationLongitude && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMapModalOpen(true)}
                  sx={{ ml: 2 }}
                >
                  Open Map
                </Button>
              )}
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  value={formData.SchoolLocationLatitude}
                  onChange={(e) => handleInputChange('SchoolLocationLatitude', e.target.value)}
                  type="number"
                  inputProps={{ step: 'any' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  value={formData.SchoolLocationLongitude}
                  onChange={(e) => handleInputChange('SchoolLocationLongitude', e.target.value)}
                  type="number"
                  inputProps={{ step: 'any' }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Statistics & Admissions */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Statistics & Admissions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Acceptance Rate (%)"
                  value={formData.acceptanceRate}
                  onChange={(e) => handleInputChange('acceptanceRate', parseFloat(e.target.value) || 0)}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Enrollment (Full-time Undergraduates)"
                  value={formData.enrollment}
                  onChange={(e) => handleInputChange('enrollment', parseInt(e.target.value) || 0)}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Room & Board (Low)"
                  value={formData.roomAndBoardLow}
                  onChange={(e) => handleInputChange('roomAndBoardLow', parseFloat(e.target.value) || 0)}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Room & Board (High)"
                  value={formData.roomAndBoardHigh}
                  onChange={(e) => handleInputChange('roomAndBoardHigh', parseFloat(e.target.value) || 0)}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Admissions Minimum Requirements"
                  value={formData.admissionsMinimum}
                  onChange={(e) => handleInputChange('admissionsMinimum', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              
              {/* US-specific admission fields */}
              {selectedCountry === 'US' && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="ACT Requirements"
                      value={formData.SchoolAdmissionsACT}
                      onChange={(e) => handleInputChange('SchoolAdmissionsACT', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="GPA Requirements"
                      value={formData.SchoolAdmissionsGPA}
                      onChange={(e) => handleInputChange('SchoolAdmissionsGPA', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="SAT Requirements"
                      value={formData.SchoolAdmissionsSAT}
                      onChange={(e) => handleInputChange('SchoolAdmissionsSAT', e.target.value)}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School Basic Requirements"
                  value={Array.isArray(formData.admissionRequirements) ? formData.admissionRequirements.join(', ') : (formData.admissionRequirements || '')}
                  onChange={(e) => handleInputChange('admissionRequirements', e.target.value.split(', ').filter(item => item.trim()))}
                  multiline
                  rows={3}
                  helperText="Enter requirements separated by commas"
                />
              </Grid>
            </Grid>
          </Box>
          </TabPanel>

          {/* Sports Tab */}
          <TabPanel value={activeTab} index={1}>
            {/* Athletics Information */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Athletics Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Athletics Nickname"
                  value={formData.athleticsNickname}
                  onChange={(e) => handleInputChange('athleticsNickname', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Athletics Website"
                  value={formData.athleticsWebsite}
                  onChange={(e) => handleInputChange('athleticsWebsite', e.target.value)}
                  type="url"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Sports Section */}
          {formData.name && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  Sports
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addSport}
                    disabled={sportsLoading}
                  >
                    Add Sport
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveSports}
                    disabled={sportsLoading || sportsSaving}
                  >
                    {sportsSaving ? 'Saving...' : 'Save Sports'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={cancelSportsChanges}
                    disabled={sportsLoading || sportsSaving}
                  >
                    Cancel Sports Changes
                  </Button>
                </Box>
              </Box>

              {/* Import AI Drafts Section */}
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Import AI drafts for:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select School</InputLabel>
                    <Select
                      value={selectedAthleticsDraftSchool}
                      onChange={(e) => setSelectedAthleticsDraftSchool(e.target.value)}
                      label="Select School"
                      disabled={athleticsDraftLoading}
                    >
                      {athleticsDraftSchools.map((school) => (
                        <MenuItem key={school.id} value={school.id}>
                          {school.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={importAthleticsDraft}
                    disabled={!selectedAthleticsDraftSchool || athleticsDraftLoading}
                  >
                    Import
                  </Button>
                </Box>
              </Box>

              {sportsLoading ? (
                <Typography>Loading sports...</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sport</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>School</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sports.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No sports found. Click "Add Sport" to add sports for this school.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sports.map((sport, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                value={sport.AthleticsSport}
                                onChange={(e) => updateSport(index, 'AthleticsSport', e.target.value)}
                                placeholder="Enter sport name"
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={sport.AthleticsGender}
                                  onChange={(e) => updateSport(index, 'AthleticsGender', e.target.value)}
                                >
                                  <MenuItem value="M">Male</MenuItem>
                                  <MenuItem value="W">Female</MenuItem>
                                  <MenuItem value="Both">Both</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sport.AthleticsSchoolName || formData.name} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => removeSport(index)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          </TabPanel>

          {/* Programs Tab */}
          <TabPanel value={activeTab} index={2}>
            {/* Programs Section */}
          {formData.name && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  Programs
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addProgram}
                    disabled={programsLoading}
                  >
                    Add Program
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={savePrograms}
                    disabled={programsLoading || programsSaving}
                  >
                    {programsSaving ? 'Saving...' : 'Save Programs'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={cancelProgramsChanges}
                    disabled={programsLoading || programsSaving}
                  >
                    Cancel Programs Changes
                  </Button>
                </Box>
              </Box>

              {/* Import AI Drafts Section */}
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Import AI drafts for:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select School</InputLabel>
                    <Select
                      value={selectedProgramsDraftSchool}
                      onChange={(e) => setSelectedProgramsDraftSchool(e.target.value)}
                      label="Select School"
                      disabled={programsDraftLoading}
                    >
                      {programsDraftSchools.map((school) => (
                        <MenuItem key={school.id} value={school.id}>
                          {school.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={importProgramsDraft}
                    disabled={!selectedProgramsDraftSchool || programsDraftLoading}
                  >
                    Import
                  </Button>
                </Box>
              </Box>

              {programsLoading ? (
                <Typography>Loading programs...</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Program Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Program Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>School</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {programs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No programs found. Click "Add Program" to add programs for this school.
                          </TableCell>
                        </TableRow>
                      ) : (
                        programs.map((program, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                value={program.ProgramName}
                                onChange={(e) => updateProgram(index, 'ProgramName', e.target.value)}
                                placeholder="Enter program name"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                value={program.ProgramCategory}
                                onChange={(e) => updateProgram(index, 'ProgramCategory', e.target.value)}
                                placeholder="Enter program category"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={program.ProgramSchoolName || formData.name} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  color={program.isInFirebase ? "success" : "primary"}
                                  title={program.isInFirebase ? "Program exists in Firebase" : "Program needs review"}
                                  onClick={() => openProgramModal(program)}
                                >
                                  {program.isInFirebase ? <CheckCircleIcon /> : <SearchIcon />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => removeProgram(index)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          </TabPanel>

          {/* Events Tab */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Events
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Events functionality will be added in a future update.
              </Typography>
            </Box>
          </TabPanel>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              size="large"
              color="error"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save School'}
            </Button>
          </Box>
        </form>
      </Paper>
      
      {/* Success/Error Message Snackbar */}
      <Snackbar
        open={!!saveMessage}
        autoHideDuration={6000}
        onClose={() => setSaveMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaveMessage(null)} 
          severity={saveMessage?.type} 
          sx={{ width: '100%' }}
        >
          {saveMessage?.text}
        </Alert>
      </Snackbar>

      {/* Map Modal */}
      <Dialog 
        open={mapModalOpen} 
        onClose={() => setMapModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            Location: {formData.name || 'School Location'}
          </Typography>
          <Button
            onClick={() => setMapModalOpen(false)}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1 }}>
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            position: 'relative',
            '& iframe': {
              border: 'none',
              width: '100%',
              height: '100%'
            }
          }}>
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${formData.SchoolLocationLatitude},${formData.SchoolLocationLongitude}&zoom=15`}
              title={`Map showing location for ${formData.name || 'school'}`}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setMapModalOpen(false)} 
            variant="outlined"
          >
            Close
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              window.open(`https://www.google.com/maps/search/?api=1&query=${formData.SchoolLocationLatitude},${formData.SchoolLocationLongitude}`, '_blank');
            }}
          >
            Open in Google Maps
          </Button>
        </DialogActions>
      </Dialog>

      {/* Program Modal */}
      <Dialog 
        open={programModalOpen} 
        onClose={closeProgramModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '800px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            Add / Edit Program
          </Typography>
          <Button
            onClick={closeProgramModal}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {editingProgram && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Program Name"
                      value={editingProgram.ProgramName}
                      onChange={(e) => handleProgramModalInputChange('ProgramName', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Program Category</InputLabel>
                      <Select
                        value={editingProgram.ProgramCategory}
                        onChange={(e) => handleProgramModalInputChange('ProgramCategory', e.target.value)}
                        label="Program Category"
                      >
                        {programCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Program Code"
                      value={editingProgram.ProgramCode || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramCode', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Credential"
                      value={editingProgram.ProgramDegree || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramDegree', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Program Description"
                      value={editingProgram.ProgramDescription || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramDescription', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Tuition Information */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Tuition Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Domestic Tuition"
                      value={editingProgram.ProgramDomesticTuition || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramDomesticTuition', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="International Tuition"
                      value={editingProgram.ProgramInternationalTuition || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramInternationalTuition', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Requirements */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Requirements
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Prerequisites"
                      value={editingProgram.ProgramPrerequisites || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramPrerequisites', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Admissions Minimum"
                      value={editingProgram.ProgramAdmissionsMinimum || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramAdmissionsMinimum', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Requirements"
                      value={editingProgram.ProgramAdditionalRequirements || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramAdditionalRequirements', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Program Details */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Program Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Language"
                      value={editingProgram.ProgramLanguage || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramLanguage', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Province/State"
                      value={editingProgram.ProgramProvinceState || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramProvinceState', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Experiential Learning"
                      value={editingProgram.ProgramExperientialLearning || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramExperientialLearning', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Program URL"
                      value={editingProgram.ProgramURL || ''}
                      onChange={(e) => handleProgramModalInputChange('ProgramURL', e.target.value)}
                      type="url"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={closeProgramModal} 
            variant="outlined"
            disabled={programModalSaving}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={saveProgramModal}
            disabled={programModalSaving || !editingProgram?.ProgramName?.trim()}
          >
            {programModalSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddEditSchools;
